import type { Condition, DesignElement, TicketDesign } from './layout.js';
import { parseDesign } from './layout.js';

/**
 * A design plus one ticket's values becomes a flat list of things to draw
 * (D-89).
 *
 * Both renderers — the PDF and the BOCA — take this list, so a design says the
 * same thing on paper at home as it does on the thermal stock at the window,
 * and there is exactly one place where a condition is evaluated, a field is
 * looked up and text is trimmed to fit.
 *
 * Conditions are evaluated here, against the same flat record of values the
 * fields come from. There is no expression to parse, so there is nothing to
 * execute: `op` is one of seven comparisons and both sides are strings.
 */

export type TicketValues = Record<string, string>;

export function conditionHolds(condition: Condition | null | undefined, values: TicketValues): boolean {
  if (!condition) return true;
  if ('all' in condition) return condition.all.every((child) => conditionHolds(child, values));
  if ('any' in condition) return condition.any.some((child) => conditionHolds(child, values));
  if ('not' in condition) return !conditionHolds(condition.not, values);
  const value = (values[condition.field] ?? '').trim();
  switch (condition.op) {
    case 'EXISTS':
      return value !== '';
    case 'NOT_EXISTS':
      return value === '';
    case 'EQUALS':
      return value.toLowerCase() === condition.value.trim().toLowerCase();
    case 'NOT_EQUALS':
      return value.toLowerCase() !== condition.value.trim().toLowerCase();
    case 'CONTAINS':
      return value.toLowerCase().includes(condition.value.trim().toLowerCase());
    case 'IN':
      return condition.values.some((candidate) => candidate.trim().toLowerCase() === value.toLowerCase());
    case 'NOT_IN':
      return !condition.values.some((candidate) => candidate.trim().toLowerCase() === value.toLowerCase());
    default:
      return true;
  }
}

/** What a text or field element prints for this ticket, after prefix, suffix and case. */
export function textFor(element: DesignElement, values: TicketValues): string {
  if (element.kind === 'TEXT') return element.uppercase ? element.text.toUpperCase() : element.text;
  if (element.kind !== 'FIELD') return '';
  const raw = (values[element.field] ?? '').trim();
  if (raw === '') {
    const fallback = element.fallback ?? '';
    return element.uppercase ? fallback.toUpperCase() : fallback;
  }
  const composed = `${element.prefix ?? ''}${raw}${element.suffix ?? ''}`;
  return element.uppercase ? composed.toUpperCase() : composed;
}

export interface ResolvedElement {
  element: DesignElement;
  /** Text elements only; already composed and cased. */
  text?: string;
  /** Codes only: what the symbol encodes. */
  payload?: string;
}

/**
 * The elements that will actually appear, in draw order.
 *
 * An element whose condition fails is gone; so is a field element that has
 * nothing to say and no fallback, because a label with no value is worse than
 * a gap — it is a gap with a colon after it.
 */
export function resolveDesign(input: unknown, values: TicketValues): { design: TicketDesign; elements: ResolvedElement[] } {
  const design = parseDesign(input);
  const elements: ResolvedElement[] = [];
  for (const element of design.elements) {
    if (!conditionHolds(element.visible_when, values)) continue;
    if (element.kind === 'TEXT' || element.kind === 'FIELD') {
      const text = textFor(element, values);
      if (text.trim() === '') continue;
      elements.push({ element, text });
      continue;
    }
    if (element.kind === 'QR') {
      const payload = element.source === 'CREDENTIAL' ? values['ticket.credential'] : element.source === 'SHORT_CODE' ? values['ticket.short_code'] : values['order.url'];
      if (!payload) continue;
      elements.push({ element, payload });
      continue;
    }
    if (element.kind === 'BARCODE') {
      const payload = element.source === 'ORDER_NUMBER' ? values['order.number'] : values['ticket.short_code'];
      if (!payload) continue;
      elements.push({ element, payload: element.symbology === 'CODE39' ? payload.toUpperCase() : payload });
      continue;
    }
    elements.push({ element });
  }
  return { design, elements };
}

// --- Units --------------------------------------------------------------------------------------

/** Mils to PDF points (72 per inch). */
export const milsToPoints = (mils: number): number => (mils / 1000) * 72;

/** Mils to printer dots at a resolution. */
export const milsToDots = (mils: number, dpi: number): number => Math.round((mils / 1000) * dpi);

/** Points to mils, for a font size expressed as a height. */
export const pointsToMils = (points: number): number => (points / 72) * 1000;
